'use client';

import { Collapse } from '@/uiComposites/interactive/Collapse';
import {
	useAccordionItemOpened,
	useDispatchAccordion,
} from '@/uiComposites/interactive/accordion/AccordionProvider.client';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { ReactNode, useId } from 'react';
import {
	stationShelf,
	stationOccupy,
	contentSegment,
	stationOpened,
	station,
	stationClosed,
} from './StepByStep.css';
import { stack } from '@/uiPrimitives/layout/stack';
import { box } from '@/uiPrimitives/layout/box';
import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { icon } from '@/uiPrimitives/media/icon';
import { delayIconTransitionHover } from '@/uiComposites/interactive/accordion/Accordion.css';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { arrange } from '@/uiPrimitives/layout/arrange';
import { translateInline } from '@/globals/translate/translateInline';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { A11YAccordionActionLabel } from '@/uiComposites/interactive/accordion/A11YAccordionActionLabel';
import { textTag } from '@/uiComposites/typography/textTag';

export const StepByStepItem = ({
	title,
	children,
	stepMarker,
	mandatory,
}: {
	title: string;
	children: ReactNode;
	stepMarker: ReactNode;
	mandatory: boolean;
}) => {
	const id = useId();
	const isOpened = useAccordionItemOpened(id);
	const dispatchAccordion = useDispatchAccordion();

	return (
		<div className={classes(contentSegment, stack({}))}>
			<button
				aria-controls={id}
				aria-expanded={isOpened}
				onClick={() => {
					dispatchAccordion({
						type: 'toggle',
						id,
					});
				}}
				{...addTestSelector(`uiAccordionItem`)}
			>
				<h2
					className={classes(
						stationShelf,
						textStyle({ color: 'positiveBlue', capSize: '1 | h3' }),
					)}
				>
					<div className={classes(stationOccupy)}>
						<div
							className={classes(
								station['main'],
								isOpened ? stationOpened : stationClosed,
							)}
						>
							{stepMarker}
						</div>
					</div>
					<div
						className={classes(
							shelf({
								alignItems: 'baseline',
								gap: '0 | p',
								gridTemplateColumns: 'minmax(0, 1fr) auto',
							}),
						)}
					>
						<div
							className={classes(
								arrange({
									layout: {
										max640px: 'stack',
										min640px: 'shelf',
									},
									rowGap: '2 | h2',
									columnGap: '0 | p',
									gridTemplateColumns: {
										min640px: 'minmax(0, 1fr) auto',
									},
									alignItems: 'start',
								}),
							)}
						>
							<div
								className={classes(
									textBlock({
										fontWeight: 'bold',
										textWrap: 'balance',
										color: isOpened ? 'positive' : undefined,
									}),
								)}
							>
								<A11YAccordionActionLabel isOpened={isOpened} />
								{title}
							</div>
							{mandatory === true && (
								<div className={classes(occupy({ blockSize: 'capSize' }))}>
									<div className={classes(textTag())}>
										{translateInline({
											en: 'Mandatory',
											nl: 'Verplicht',
										})}
									</div>
								</div>
							)}
						</div>
						<i
							className={classes(
								delayIconTransitionHover,
								icon({
									icon: chevronIcon,
									adjustIcon: 'rotate90deg',
									flip: isOpened ? 'horizontal:hover' : 'horizontalNone:hover',
									blockSize: '1ex',
								}),
							)}
						/>
					</div>
				</h2>
			</button>
			<Collapse id={id} isOpened={isOpened}>
				<div
					className={classes(
						box({
							paddingBlockStart: '5 | betweenParagraphs',
							paddingBlockEnd: 'negative 0 | p',
						}),
					)}
					{...addTestSelector(`uiAccordionContent`)}
				>
					{children}
				</div>
			</Collapse>
		</div>
	);
};
